import { el, clear } from '../../../core/dom.js';
import type { RescueModule, RescueResult } from '../../rescue-types.js';
import './pattern-pulse.css';

export const patternPulse: RescueModule = {
  id: 'pattern-pulse',
  name: 'Pattern Pulse',
  kind: 'minigame',
  defaultDurationSec: 90,
  reducedMotion: true,
  safety: {
    gamblingLike: false,
    excludeForDomains: [],
    experimental: false,
    reducedMotionSafe: true,
  },
  async run(host, ctx) {
    return new Promise<RescueResult>((resolve) => {
      clear(host);
      const stage = el('div', { class: 'rescue-stage' });
      const wrap = el('div', { class: 'pp-wrap' });
      const header = el('div', { class: 'pp-header' });
      const cue = el('div', { class: 'pp-cue' }, 'Watch');
      const roundLabel = el('div', { class: 'pp-round' }, 'Round 1');
      header.appendChild(cue);
      header.appendChild(roundLabel);
      const grid = el('div', { class: 'pad-grid' });
      const pads: HTMLButtonElement[] = [];
      for (let i = 0; i < 9; i++) {
        const p = el('button', { class: 'pad', attrs: { 'aria-label': `pad ${i + 1}` } });
        p.dataset['idx'] = String(i);
        pads.push(p);
        grid.appendChild(p);
      }
      const abandon = el('button', { class: 'btn ghost', onclick: () => done({ kind: 'abandoned' }) }, 'I’m done');
      wrap.appendChild(header);
      wrap.appendChild(grid);
      wrap.appendChild(abandon);
      stage.appendChild(wrap);
      host.appendChild(stage);

      function setCue(text: string, waiting: boolean) {
        cue.textContent = text;
        cue.classList.toggle('waiting', waiting);
      }

      let length = 3;
      let sequence: number[] = [];
      let inputIdx = 0;
      let acceptingInput = false;
      let rounds = 0;

      const startedAt = Date.now();
      const minDur = ctx.durationSec * 1000;

      function newRound() {
        sequence = [];
        for (let i = 0; i < length; i++) sequence.push(Math.floor(Math.random() * 9));
        inputIdx = 0;
        playSequence();
      }
      async function playSequence() {
        acceptingInput = false;
        setCue('Watch', false);
        roundLabel.textContent = `Round ${rounds + 1}`;
        for (const idx of sequence) {
          await wait(420);
          const pad = pads[idx];
          if (pad) flash(pad);
        }
        await wait(300);
        setCue('Your turn', true);
        acceptingInput = true;
      }
      const flashTimers = new WeakMap<HTMLButtonElement, number>();
      function pulse(p: HTMLButtonElement, cls: 'lit' | 'good' | 'bad', durMs: number) {
        const prev = flashTimers.get(p);
        if (prev !== undefined) window.clearTimeout(prev);
        p.classList.remove('lit', 'good', 'bad');
        void p.offsetWidth;
        p.classList.add(cls);
        const id = window.setTimeout(() => {
          p.classList.remove(cls);
          flashTimers.delete(p);
        }, durMs);
        flashTimers.set(p, id);
      }
      function flash(p: HTMLButtonElement) { pulse(p, 'lit', 520); }
      function wait(ms: number) { return new Promise((r) => setTimeout(r, ms)); }

      grid.addEventListener('click', (e) => {
        const t = (e.target as HTMLElement).closest('.pad') as HTMLButtonElement | null;
        if (!t || !acceptingInput) return;
        const idx = Number(t.dataset['idx']);
        if (sequence[inputIdx] !== idx) {
          acceptingInput = false;
          pulse(t, 'bad', 560);
          setCue('Try again', false);
          inputIdx = 0;
          setTimeout(playSequence, 800);
          return;
        }
        pulse(t, 'good', 460);
        inputIdx++;
        if (inputIdx >= sequence.length) {
          acceptingInput = false;
          rounds++;
          length = Math.min(7, length + 1);
          if (Date.now() - startedAt >= minDur && rounds >= 3) {
            setCue('Nicely done', false);
            setTimeout(() => done({ kind: 'completed', details: { rounds } }), 600);
            return;
          }
          setCue('Got it', false);
          setTimeout(newRound, 600);
        }
      });

      newRound();

      function done(res: RescueResult) {
        if (timerHandle) clearInterval(timerHandle);
        resolve(res);
      }

      // Hard cap at 4 minutes
      const timerHandle = window.setTimeout(() => done({ kind: 'completed', details: { rounds, capped: true } }), Math.max(minDur * 2, 240_000));
    });
  },
};
